import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { PendingProductChange, ProductScanResult } from '../models/pending-product-change.model';
import { PaginatedResult } from '../models/paginated-result.model';
import { ApiType, ServiceBase } from '../shared/classes/serviceBase';
import { environment } from '../../environments/environment';

/**
 * Seller product changes awaiting review — backed by Retail.API
 * /api/admin/product-changes (platform admin). Approving applies the reviewed
 * snapshot to the marketplace catalog; the scan endpoint queues fresh seller
 * catalog changes on demand (the background sweep does the same on a timer).
 */
@Injectable({ providedIn: 'root' })
export class PendingProductChangeService extends ServiceBase {
  private readonly endpoint = 'api/admin/product-changes';
  /** Server-enforced ceiling: admin lists return at most 12 rows per request. */
  static readonly PAGE_SIZE = 12;

  constructor(http: HttpClient, router: Router) {
    super(http, router);
  }

  protected override getBusinessIdValue(): string {
    return environment.marketplaceBusinessId;
  }

  protected override getTerminalIdValue(): string {
    return environment.marketplaceTerminalId;
  }

  getPendingChanges(pageNumber: number): Observable<PaginatedResult<PendingProductChange>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', PendingProductChangeService.PAGE_SIZE)
      .set('status', 'Pending');
    return this.get<PaginatedResult<PendingProductChange>>(`${this.endpoint}/GetChanges`, ApiType.RETAIL, params);
  }

  approveChange(id: string): Observable<PendingProductChange> {
    return this.post<PendingProductChange>(`${this.endpoint}/${encodeURIComponent(id)}/approve`, {}, ApiType.RETAIL, {
      businessId: this.getBusinessIdValue(),
      terminalId: this.getTerminalIdValue()
    });
  }

  rejectChange(id: string, reason: string): Observable<PendingProductChange> {
    return this.post<PendingProductChange>(`${this.endpoint}/${encodeURIComponent(id)}/reject`, { reason }, ApiType.RETAIL, {
      businessId: this.getBusinessIdValue(),
      terminalId: this.getTerminalIdValue()
    });
  }

  /** Scan seller catalogs for new/changed items now instead of waiting for the sweep. */
  runScan(): Observable<ProductScanResult> {
    return this.post<ProductScanResult>(`${this.endpoint}/scan`, {}, ApiType.RETAIL, {
      businessId: this.getBusinessIdValue(),
      terminalId: this.getTerminalIdValue()
    });
  }
}
